<template>
	<v-expansion-panel class="question-expander">
		<v-expansion-panel-header>
			<template v-slot:default="{ open }">
				<!-- question text -->
				<div v-if="!open">
					<div
						v-html="question.text || 'نص السؤال'"
						class="question-title font-weight-medium"
					/>

					<div class="d-flex align-center text-caption mt-2">
						<div class="me-2 ">
							<v-icon size="16" class="me-1">mdi-progress-question</v-icon>
							{{questionTypes.find(c => c.id === questionType.single)?.name || 'نوع السؤال'}}
						</div>
						<div class="me-2 text-caption">
							<v-icon size="16" class="me-1">mdi-card-multiple-outline</v-icon>
							{{question.options.length}} خيارات
						</div>
						<div v-if="question.image">
							<v-icon size="16" class="me-1">mdi-image-outline</v-icon>
							تم إرفاق صورة
						</div>
					</div>
				</div>

				<!-- question type -->
				<v-chip
					v-else
					color="grey rounded lighten-2"
					outlined
					style="max-width: fit-content;"
				>
					<span class="black--text font-weight-bold">
						{{questionTypes.find(c => c.id === questionType.single)?.name || 'نوع السؤال'}}
					</span>
				</v-chip>
			</template>
		</v-expansion-panel-header>
		<v-expansion-panel-content eager>
			<!-- question text -->
			<p class="mb-2">نص السؤال</p>
			<div class="d-flex align-start">
				<!-- image -->
				<bee-drop-img
					v-model="question.image"
					:width="92"
					:height="92"
					clearable
					dashed
					:border-width="2"
					border-color="#cfcfcf"
					drag-text="صورة"
					drop-text="أفلت"
					blurable
					hoverable
					hide-image-name
					hide-details
					:radius="10"
				/>

				<!-- text -->
				<editor
					v-model="question.text"
					ref="editor"
					hide-menubar
					placeholder="Write the question here ..."
					mini-tools
					class="flex-grow-1"
					:error="error && !question.text"
				/>
			</div>
			
			<!-- options -->
			<p class="mt-4 mb-2">الخيارات</p>
			<div>
				<div
					v-for="(option, i) in question.options"
					:key="option.id"
					class="d-flex align-center mb-2"
				>
					<!-- remove button -->
					<v-btn
						v-if="question.options.length > 1"
						icon
						class="me-1"
						tabindex="-1"
						@click="
							question.options.splice(i, 1);
							question.options.forEach((c, i) => c.id > option.id ? c.id-- : null);
						"
					>

						<v-icon>$close</v-icon>
					</v-btn>

					<!-- question options -->
					<v-card
						outlined
						dir="ltr"
						class="d-flex align-center flex-grow-1 px-2 py-0"
						:class="{ [errorClass]: error && !option.text }"
					>
						<!-- check -->
						<v-btn
							tabindex="-1"
							icon
							:ripple="false"
							@click="() => {
								const checkOption =
									question.options.find(c =>
										c.id !== option.id && c.isCorrect
									);
								if (checkOption) checkOption.isCorrect = false;
								option.isCorrect = true;
							}"
						>
							<v-icon
								:color="
									option.isCorrect
										? 'success'
										: (
											error &&
											!question.options.some(c => c.isCorrect)
												? 'error'
												: null
										)"
							>
								{{
									option.isCorrect
										? 'mdi-check-circle-outline'
										: 'mdi-circle-outline'
								}}
							</v-icon>
						</v-btn>
				
						<!-- input -->
						<input
							v-model="option.text"
							:ref="`optionInput-${i}`"
							placeholder="Enter the option text here ..."
							class="full-input rounded-0 px-1 py-2"
							@keydown.backspace="
								!option.text && question.options.length > 1
									? (() => {
										$event.preventDefault();
										question.options.splice(i, 1);
										question.options.forEach((c, i) => c.id > option.id ? c.id-- : null)
										$nextTick(() => {
											$refs[`optionInput-${i >= 1 ? i - 1 : i}`][0]?.focus()
										})
									})()
									: null;
							"
							@keydown.enter="
								question.options.splice(i + 1, 0, {
									id: question.options.length,
									text: '',
									isCorrect: false
								});
								$nextTick(() => {
									$refs[`optionInput-${i + 1}`][0].focus();
								})
							"
						>
					</v-card>
				</div>
			</div>
			<v-btn
				text
				small
				color="primary"
				@click="question.options.push({
					id: question.options.length,
					text: '',
					isCorrect: false
				})"

			>
				<v-icon size="18" class="me-2">$add</v-icon>
				إضافة خيار جديد
			</v-btn>

			<template v-if="!hideDeleteBtn">
				<v-divider class="my-3"/>
				
				<v-btn
					color="grey lighten-2"
					outlined
					@click="$emit('click:delete')"
				>
					<span class="error--text">
						<v-icon size="18">$delete</v-icon>
						حذف السؤال
					</span>
				</v-btn>
			</template>
		</v-expansion-panel-content>
	</v-expansion-panel>
</template>

<script>
import { questionType, questionTypes } from '@/helpers/enums';

export default {
	name: 'VQuestionExpander',
	props: {
		hideDeleteBtn: { type: Boolean, default: false },
		error: { type: Boolean, default: false },
		errorClass: { type: String, default: 'error-border' },
		value: {
			type: Object,
			default: () => ({
				text: '',
				type: null,
				image: null,
				options: [],
			})
		}
	},

	data() {
		return {
			questionType,
			questionTypes,
			
			question: {
				text: '',
				type: null,
				image: null,
				options: [],
			}
		}
	},

	watch: {
		value: {
			handler(val) {
				this.question.text = val?.text ?? this.question.text;
				this.question.type = val?.type ?? this.question.type;
				this.question.image = val?.image ?? this.question.image;
				this.question.options = val?.options ?? this.question.options;
			},
			immediate: true,
			deep: true,
		},

		question: {
			handler() { this.updateValue(); },
			deep: true
		}
	},

	methods: {
		updateValue() {
			this.$emit('input', {
				...this.value,
				text: this.question.text || '',
				type: typeof this.question.type === 'number' ? this.question.type : null,
				image: this.question.image || null,
				options: this.question.options || [],
			});
		}
	}
}
</script>

<style lang="scss">
</style>